import React from "react";
import PropTypes from "prop-types";
import get from "lodash.get";
import styled from "@emotion/styled";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import MuiButton from "@mui/material/Button";

import { injectIntl } from "react-intl";

import RadioGroup from "components/RadioGroup";
import SelectField from "components/SelectField";

import ParkkiTheme from "themes/ParkkiTheme";
import { DatePickersSection } from "scenes/CustomAnalysis/components";

const DialogContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: ${ParkkiTheme.spacings.base};
  padding-bottom: 0;
`;

const StyledDialogActions = styled(DialogActions)`
  display: flex;
  justify-content: center;
  margin-top: ${ParkkiTheme.spacings.base};
`;

const Title = styled.div`
  font-size: ${ParkkiTheme.fontSizes.small};
  font-weight: bolder;
`;

const SelectContainer = styled.div`
  display: flex;
  flex: 1;
  width: 50%;
  min-width: 175px;
  max-width: 340px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-right: 7px;
`;

const RadioGroupContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  padding-bottom: -8px;
`;

const AreasContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  margin-top: ${ParkkiTheme.spacings.xsmall};
`;

class CustomAnalysisFiltersDialogComponent extends React.Component {
  getFilteredAreas = () => {
    const { areas, analysisAreaGroups } = this.props;

    const selectedGroupName =
      analysisAreaGroups?.selected?.[0] || analysisAreaGroups?.default;
    const selectedGroup = analysisAreaGroups?.groups?.find(
      (group) => group.name === selectedGroupName
    );

    if (analysisAreaGroups?.groups?.length > 1 && selectedGroup) {
      return areas.filter(
        (area) =>
          selectedGroup?.analysis_area_types
            .map((type) => type.toLowerCase())
            .indexOf(area.type.toLowerCase()) >= 0
      );
    }

    return areas;
  };

  renderActions = () => {
    const { intl } = this.props;
    const areas = this.getFilteredAreas();

    return (
      <StyledDialogActions>
        <MuiButton variant="text" color="secondary" onClick={this.handleClose}>
          {intl.messages["components.buttons.cancel"]}
        </MuiButton>

        <MuiButton
          variant="text"
          label={intl.messages["components.buttons.search"]}
          color="primary"
          onClick={this.updateFilters}
          disabled={areas.length === 0}
        >
          {intl.messages["components.buttons.search"]}
        </MuiButton>
      </StyledDialogActions>
    );
  };

  updateFilters = () => {
    const { search } = this.props;

    return search.onClick();
  };

  handleClose = () => {
    this.props.hideDialog();
  };

  renderAnalysisAreaRadioGroup = () => {
    const { analysisAreaGroups, areas, intl, setFilter } = this.props;

    if (!analysisAreaGroups || analysisAreaGroups?.groups?.length <= 1)
      return null;

    return (
      <RadioGroupContainer>
        <RadioGroup
          inline
          title={
            intl.messages[
              "customAnalysisScene.filters.analysisAreaGroups.title"
            ]
          }
          values={analysisAreaGroups?.groups?.map((item) => ({
            ...item,
            value: item?.name,
            label:
              intl.messages[
                `customAnalysisScene.filters.analysisAreaGroups.${item?.name}`
              ],
          }))}
          value={
            analysisAreaGroups?.selected?.[0] || analysisAreaGroups?.default
          }
          onChange={(event, value) => {
            const selectedGroups = analysisAreaGroups?.groups?.find(
              (group) => group?.name === value
            );
            const areasFromSelectedGroup = areas.filter(
              (area) =>
                selectedGroups.analysis_area_types
                  .map((item) => item.toLowerCase())
                  .indexOf(area.type.toLowerCase()) >= 0
            );

            setFilter(
              "selectedAreas",
              areasFromSelectedGroup?.map((area) => area.id)
            );
            setFilter("analysisAreaGroups", {
              ...analysisAreaGroups,
              selected: [value],
            });
          }}
        />
      </RadioGroupContainer>
    );
  };

  renderAreasSelect = () => {
    const { intl, setFilter } = this.props;
    const filteredAreas = this.getFilteredAreas();

    return (
      <SelectContainer>
        <SelectField
          checkbox
          id="areas-filters"
          label={intl.messages["customAnalysisScene.filters.analyzedAreas"]}
          values={filteredAreas}
          checked={this.getSelectedAreas()}
          onChange={(event) => {
            if (get(event, "target.value", []).length === 0) {
              return;
            }

            return (
              get(event, "target.value") &&
              setFilter("selectedAreas", get(event, "target.value"))
            );
          }}
        />
      </SelectContainer>
    );
  };

  getSelectedAreas = () => {
    const { areas } = this.props;
    return areas.filter((area) => area.selected).map((area) => area.id);
  };

  renderDatePickers = () => {
    const {
      startDate,
      endDate,
      comparedStartDate,
      comparedEndDate,
      withComparison,
    } = this.props;

    return (
      <DatePickersSection
        startDate={startDate}
        endDate={endDate}
        comparedStartDate={comparedStartDate}
        comparedEndDate={comparedEndDate}
        withComparison={withComparison}
      />
    );
  };

  render() {
    const { intl, open } = this.props;

    return (
      <Dialog maxWidth="md" open={open} onClose={this.handleClose}>
        <DialogContainer>
          <Title>
            {intl.messages["customAnalysisScene.title"].toUpperCase()}
          </Title>
          <AreasContainer>
            {this.renderAnalysisAreaRadioGroup()}
            {this.renderAreasSelect()}
          </AreasContainer>
          {this.renderDatePickers()}
          {this.renderActions()}
        </DialogContainer>
      </Dialog>
    );
  }
}

CustomAnalysisFiltersDialogComponent.defaultProps = {
  open: false,
};

CustomAnalysisFiltersDialogComponent.propTypes = {
  endDate: PropTypes.shape({
    value: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
  }).isRequired,
  open: PropTypes.bool,
  startDate: PropTypes.shape({
    value: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
  }).isRequired,
  search: PropTypes.shape({
    disabled: PropTypes.bool,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
  }).isRequired,
};

const CustomAnalysisFiltersDialog = injectIntl(
  CustomAnalysisFiltersDialogComponent
);
export { CustomAnalysisFiltersDialog };
